/**
 * Semente do bloco "Imprensa".
 *
 * O dado de contato das ascoms mora no bloco (e não num vocabulário): o
 * `ministerio.publico.federal.unidades_origem` só conhece código e título das
 * unidades — telefone e e-mail de assessoria não existem no backend. A lista
 * abaixo entra por `initialValue` na hora em que o bloco é inserido (ver
 * `config/blocks/content.ts`) e, dali em diante, é o editor quem manda: quem
 * apagar ou corrigir uma linha na barra lateral não a vê voltar.
 *
 * Ordem: PGR primeiro (sede), depois as PRRs e as PRs, na mesma ordem da
 * página de origem.
 */

export interface ImprensaContato {
  /**
   * Identificador da linha na lista.
   *
   * Não é uma URL: é a chave que o `ObjectListWidget` do core passa ao
   * drag-and-drop (`childList={value.map((o) => [o['@id'], o])}`). Item sem
   * `@id` some da barra lateral — por isso a semente já nasce com ele.
   */
  '@id'?: string;
  /** Sigla exibida na linha, ex.: `PR/AC`. */
  unidade: string;
  /** Nome por extenso. Não aparece na linha — serve só para a busca casar
   *  "Acre" com `PR/AC`. */
  nome?: string;
  /** Como o número é exibido. Pode trazer dois ramais (`… / 0148`). */
  telefone?: string;
  email?: string;
  /** Cidade da sala de atendimento, exibida na linha antes dos canais. */
  cidade?: string;
  /** Endereço da sala de atendimento, numa linha própria abaixo dos canais. */
  endereco?: string;
  /**
   * Página com endereço e horário da unidade. Com ela, a linha ganha o link
   * de rótulo `contatosLinkRotulo` — é o desenho das "Salas de atendimento
   * ao cidadão" na página de Serviços.
   */
  link?: string;
}

/** `PR/AC` -> `pr-ac`: legível, estável e único dentro da lista. */
export const contatoId = (unidade: string): string =>
  unidade
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

/**
 * Preenche o `@id` que falta, sem tocar no que já tem.
 *
 * Vale tanto para a semente quanto para uma lista que chegou de fora (import
 * de conteúdo, JSON escrito à mão) — ver `Data.tsx`.
 */
export const comContatoIds = (contatos: ImprensaContato[]): ImprensaContato[] =>
  contatos.map((contato) =>
    contato?.['@id']
      ? contato
      : { '@id': contatoId(contato?.unidade ?? ''), ...contato },
  );

const CONTATOS: ImprensaContato[] = [
  {
    unidade: 'PGR',
    nome: 'Procuradoria-Geral da República',
    telefone: '(61) 3105-5100',
    email: 'pgr-imprensa@mpf.mp.br',
  },
  {
    unidade: 'PRR1',
    nome: 'Procuradoria Regional da República da 1ª Região',
    telefone: '(61) 3317-4864',
    email: 'prr1-ascom@mpf.mp.br',
  },
  {
    unidade: 'PRR2',
    nome: 'Procuradoria Regional da República da 2ª Região',
    telefone: '(21) 3554-9003',
    email: 'prr2-ascom@mpf.mp.br',
  },
  {
    unidade: 'PRR3',
    nome: 'Procuradoria Regional da República da 3ª Região',
    telefone: '(11) 2192-8620',
    email: 'prr3-ascom@mpf.mp.br',
  },
  {
    unidade: 'PRR4',
    nome: 'Procuradoria Regional da República da 4ª Região',
    telefone: '(51) 3216-2016',
    email: 'prr4-ascom@mpf.mp.br',
  },
  {
    unidade: 'PRR5',
    nome: 'Procuradoria Regional da República da 5ª Região',
    telefone: '(81) 2121-9966',
    email: 'prr5-ascom@mpf.mp.br',
  },
  {
    unidade: 'PRR6',
    nome: 'Procuradoria Regional da República da 6ª Região',
    telefone: '(31) 2123-9202',
    email: 'prr6-imprensa@mpf.mp.br',
  },
  {
    unidade: 'PR/AC',
    nome: 'Acre',
    telefone: '(68) 3214-1430',
    email: 'prac-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/AL',
    nome: 'Alagoas',
    telefone: '(82) 2121-1485',
    email: 'pral-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/AM',
    nome: 'Amazonas',
    telefone: '(92) 2129-4734',
    email: 'pram-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/AP',
    nome: 'Amapá',
    telefone: '(96) 3213-7895',
    email: 'prap-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/BA',
    nome: 'Bahia',
    telefone: '(71) 3617-2295',
    email: 'prba-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/CE',
    nome: 'Ceará',
    telefone: '(85) 3266-7457',
    email: 'prce-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/DF',
    nome: 'Distrito Federal',
    telefone: '(61) 3313-5460',
    email: 'prdf-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/ES',
    nome: 'Espírito Santo',
    telefone: '(27) 3211-6444',
    email: 'pres-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/GO',
    nome: 'Goiás',
    telefone: '(62) 3243-5454',
    email: 'prgo-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/MA',
    nome: 'Maranhão',
    telefone: '(98) 3213-7137',
    email: 'prma-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/MS',
    nome: 'Mato Grosso do Sul',
    telefone: '(67) 3312-7283',
    email: 'prms-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/MT',
    nome: 'Mato Grosso',
    telefone: '(65) 3612-3155',
    email: 'prmt-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/MG',
    nome: 'Minas Gerais',
    telefone: '(31) 2123-9008',
    email: 'prmg-imprensa@mpf.mp.br',
  },
  {
    unidade: 'PR/PA',
    nome: 'Pará',
    telefone: '(91) 3299-0212 / 0148',
    email: 'prpa-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/PR',
    nome: 'Paraná',
    telefone: '(41) 3219-8843',
    email: 'prpr-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/PB',
    nome: 'Paraíba',
    telefone: '(83) 3044-6281',
    email: 'prpb-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/PE',
    nome: 'Pernambuco',
    telefone: '(81) 2125-7300 / 7348',
    email: 'prpe-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/PI',
    nome: 'Piauí',
    telefone: '(86) 3214-5987',
    email: 'prpi-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/RJ',
    nome: 'Rio de Janeiro',
    telefone: '(21) 3971-9570',
    email: 'prrj-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/RN',
    nome: 'Rio Grande do Norte',
    telefone: '(84) 3232-3801',
    email: 'prrn-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/RS',
    nome: 'Rio Grande do Sul',
    telefone: '(51) 3284-7200',
    email: 'prrs-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/RO',
    nome: 'Rondônia',
    telefone: '(69) 3216-0586',
    email: 'prro-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/RR',
    nome: 'Roraima',
    telefone: '(95) 3198-2045 / 2034',
    email: 'prrr-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/SC',
    nome: 'Santa Catarina',
    telefone: '(48) 2107-2466',
    email: 'prsc-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/SE',
    nome: 'Sergipe',
    telefone: '(79) 3301-3837',
    email: 'prse-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/SP',
    nome: 'São Paulo',
    telefone: '(11) 3269-5469',
    email: 'prsp-ascom@mpf.mp.br',
  },
  {
    unidade: 'PR/TO',
    nome: 'Tocantins',
    telefone: '(63) 3219-7238',
    email: 'prto-ascom@mpf.mp.br',
  },
];

export const CONTATOS_PADRAO: ImprensaContato[] = comContatoIds(CONTATOS);

/** Dado do bloco recém-inserido. Ver `ImprensaData` em `./schema`. */
export const IMPRENSA_INITIAL_VALUE = {
  titulo: 'Imprensa',
  atendimentoTitulo: 'Atendimento a jornalistas',
  atendimentoTexto:
    'A assessoria de imprensa atende solicitações de jornalistas em dias úteis.',
  telefone: '(61) 3105-5300',
  email: 'imprensa@mpf.mp.br',
  horario: 'Segunda a sexta, 9h às 18h',
  contatosTitulo: 'Contatos por estado',
  contatosLabel: 'Estado',
  contatosPlaceholder: 'Busque por estado ou sigla',
  contatos: CONTATOS_PADRAO,
};
